import { DotsVertical, Target01 } from '@untitledui/icons';
import { useState } from 'react';
import type { MapRef } from 'react-map-gl/mapbox';
import { IconButton } from '../../../components/buttons/IconButton';
import ConfirmCloseServiceDialog from '../../../components/dialogs/ConfirmCloseServiceDialog';
import ConfirmOpenServiceDialog from '../../../components/dialogs/ConfirmOpenServiceDialog';
import ConfirmRemoveCenterDialog from '../../../components/dialogs/ConfirmRemoveCenterDialog';
import UssLogsDialog from '../../../components/dialogs/UssLogsDialog';
import MenuDivider from '../../../components/general/MenuDivider';
import MenuItemTile from '../../../components/general/MenuItemTile';
import { Text } from '../../../components/general/Text';
import { getServerName, getServerNameCode } from '../../../config/gcs/gcsConfigs';
import { useFocusCenter } from '../../../hooks/useFocusCenter';
import { en } from '../../../locales/en';
import { useDialogQueueStore } from '../../../store/useDialogQueueStore';
import { useGcsStore } from '../../../store/useGcsStore';
import { CenterOpenState } from '../../../types/gcs/center';
import { Disconnected } from '../../../types/general/connectionState';
import CenterOpenStateIcon from './CenterOpenStateIcon';
import ConnectionStatusIcon from './ConnectionStatusIcon';
import DropDownMenu from '../../../components/general/DropDownMenu';

interface CenterTileProps {
  gcsId: string;
  mapRef?: React.RefObject<MapRef | null>;
}

function CenterTile({ gcsId, mapRef }: CenterTileProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const focusCenter = useFocusCenter(mapRef ?? { current: null }, gcsId);

  const centerOpenState = useGcsStore((state) => {
    const gcsData = state.data.get(gcsId);
    return gcsData?.centerOpenState || null;
  });

  const connectionState = useGcsStore((state) => {
    const gcsData = state.data.get(gcsId);
    return gcsData?.connectionState ?? new Disconnected();
  });

  const { showDialog, hideDialog } = useDialogQueueStore();

  const nameCode = getServerNameCode(gcsId);
  const fullName = getServerName(gcsId);

  const onUssLogsClicked = () => {
    setIsDropdownOpen(false);
    showDialog({
      id: 'uss-logs-dialog',
      component: <UssLogsDialog gcsId={gcsId} onClose={hideDialog} />
    });
  };

  const onCloseServiceClicked = () => {
    setIsDropdownOpen(false);
    showDialog({
      id: 'confirm-close-service-dialog',
      component: <ConfirmCloseServiceDialog gcsId={gcsId} onClose={hideDialog} />
    });
  };

  const onOpenServiceClicked = () => {
    setIsDropdownOpen(false);
    showDialog({
      id: 'confirm-open-service-dialog',
      component: <ConfirmOpenServiceDialog gcsId={gcsId} onClose={hideDialog} />
    });
  };

  const onRemoveCenterClicked = () => {
    if (!mapRef) return;
    setIsDropdownOpen(false);
    showDialog({
      id: `confirm-remove-center-dialog-${gcsId}`,
      component: <ConfirmRemoveCenterDialog gcsId={gcsId} mapRef={mapRef} onClose={hideDialog} />
    });
  };

  return (
    <div
      style={{
        backgroundColor: 'white',
        border: 'var(--card-border-stroke) var(--card-border-style) var(--card-border-color)',
        borderRadius: 'var(--card-border-radius)',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '8px'
      }}
    >
      {/* Column with Name Code */}
      <div
        style={{
          display: 'flex',
          flex: 1,
          alignItems: 'flex-start'
        }}
      >
        <Text variant='body2' title={fullName}>
          {nameCode}
        </Text>
      </div>

      {/* Connection Status Icon */}
      <ConnectionStatusIcon connectionState={connectionState} />

      {/* Center Open State Icon */}
      <CenterOpenStateIcon centerOpenState={centerOpenState?.openState || null} />

      {/* Focus Center Button */}
      <IconButton icon={Target01} onClick={focusCenter} title={en.centersPanel.focusCenter} />

      {/* Menu Button */}
      <IconButton
        icon={DotsVertical}
        onClick={() => setIsDropdownOpen((prev) => !prev)}
        title={en.sidePanelMenuItems.moreOptions}
      />

      {/* Dropdown Menu */}
      <DropDownMenu isOpen={isDropdownOpen} onClose={() => setIsDropdownOpen(false)}>
        {centerOpenState?.openState === CenterOpenState.CENTER_OPEN && (
          <MenuItemTile onClick={onCloseServiceClicked} label={en.centersPanelMenuItems.confirmCloseService} />
        )}
        {(centerOpenState?.openState === CenterOpenState.CENTER_OPEN_SERVICE_CLOSED ||
          centerOpenState?.openState === CenterOpenState.CENTER_OPEN_CAN_BE_CLOSED) && (
          <MenuItemTile onClick={onOpenServiceClicked} label={en.centersPanelMenuItems.confirmOpenService} />
        )}
        <MenuItemTile onClick={onUssLogsClicked} label={en.centersPanelMenuItems.ussLogs} />
        <MenuDivider />
        <MenuItemTile
          onClick={onRemoveCenterClicked}
          label={en.centersPanelMenuItems.removeCenter}
          textColor='var(--color-danger)'
          hoverBackgroundColor='var(--color-danger-hover)'
        />
      </DropDownMenu>
    </div>
  );
}

export default CenterTile;
